/**
 * §1 Baseline 2025 — the pure shaping behind the survey's charts.
 *
 * Same division of labour as `dashboard.ts`: everything the section decides about *numbers*
 * is here, without React, so it can be tested as functions rather than through a DOM.
 * `BaselineSection.tsx` composes, `BaselineCharts.tsx` draws, and this file answers four
 * questions, all of which the client's 2026-09-16 direction — *"sections per sheet, each
 * sub-indicator with its own full chart, mixed chart types, all responding to filters, clear
 * not compact"* — turned into real decisions:
 *
 * 1. **How does one estimate print?** — `fmtValue`, through `fmtNum` with the server's own
 *    decimal count, so a survey percentage and a KPI are formatted by one function in one
 *    locale. It is the one helper that survived the rebuild unchanged.
 * 2. **Which chart does a series get?** — `chartPlan`. The survey is not 58 independent
 *    numbers: some of its series are one question's two answers (training received, women
 *    and men) and some are one question's four mutually exclusive answers (land tenure:
 *    owned, pledged, rented, borrowed). Drawing those as separate bar charts throws away the
 *    only interesting thing about them, which is that they are parts of one whole.
 * 3. **What is on the category axis?** — `baselineAxis`. Either the four programme countries
 *    or the surveyed watersheds, narrowed by the page's own scope.
 * 4. **What is a series' value for one row of that axis?** — `axisValue`, the only place
 *    that knows `by_country` is keyed by ISO3 and `by_watershed` by watershed id.
 *
 * (It holds the chart *planning* rather than living in its own `baselineCharts.ts` for a
 * flat-footed reason: `BaselineCharts.tsx` and `baselineCharts.ts` are the same filename to
 * a case-insensitive filesystem's module resolver, and one of the two has to give.)
 */
import type {
  DashboardBaseline,
  DashboardBaselineSeries,
  DashboardBaselineSheet,
  DashboardBaselineWatershedRef,
} from '../../lib/api';
import { EMDASH, fmtNum } from '../../lib/format';
import { currentLanguage } from '../../lib/i18n';
import { countryColor, COUNTRY_ORDER } from '../../lib/palette';
import { round, signed } from './DashboardCards';

/**
 * One survey estimate as the reader sees it: `33.8%` in English, `33,8 %` in French, or `—`.
 *
 * The `%` rule is `lib/format.ts`'s own `fmtPct` — the sign closed up against the number —
 * because a baseline share and an §F7 percentage are the same kind of figure and printing
 * them two ways on one page is a typographic accident, not a distinction. French keeps the
 * narrow no-break space its typography asks for (U+202F, which never wraps). Every other
 * unit — `TLU`, `kg/ha`, `USD` — is a word after the number and takes a normal space.
 */
export function fmtValue(value: number | null, fmt: number, unit: string): string {
  if (value === null) return EMDASH;
  const num = fmtNum(value, fmt);
  if (!unit) return num;
  if (unit === '%') return currentLanguage() === 'fr' ? `${num}\u202f%` : `${num}%`;
  return `${num} ${unit}`;
}

/**
 * §3 — what one series has done since the survey's nearest earlier round, at this scope.
 *
 * `null` whenever there is nothing honest to say: no earlier round for the series, or either
 * end of the comparison unmeasured. A survey estimate against a missing one is not a change
 * of zero, and a card that printed one would invent a flat trend out of a gap in coverage.
 *
 * The figure is rounded to the series' own decimals **before** its sign is chosen, so a move
 * that prints as `0` is never given a `+` — the same rule the KPI rail's delta follows.
 */
export interface BaselineDelta {
  /** The rounded change, in the series' own unit. */
  value: number;
  /** The round it is measured against. */
  period: number;
  /**
   * `+3.7` / `−3,7` for a share, `+120 kg/ha` for an amount — ready to print.
   *
   * A share's text carries **no unit**, because the unit of the change is not the unit of
   * the level: see `share` below. The sentence the caller wraps it in supplies the `pp`.
   */
  text: string;
  /**
   * Whether this is a difference between two **percentages**, and therefore in points.
   *
   * 30 % to 33.7 % is a rise of 3.7 *percentage points*, not of 3.7 % — that would be a
   * rise to 31.1 %. The card printed `+3.7%` and said the wrong thing about every share in
   * the survey, which is 50 of its 58 series. `BaselineCharts` reads this to choose
   * `dashboard.baseline.deltaShare` ("+3.7 pp since 2023") over `delta`.
   */
  share: boolean;
  direction: 'up' | 'down' | '';
}

export function seriesDelta(series: DashboardBaselineSeries): BaselineDelta | null {
  const previous = series.previous;
  if (!previous || typeof previous.value !== 'number' || typeof series.value !== 'number') {
    return null;
  }
  if (!Number.isFinite(previous.value) || !Number.isFinite(series.value)) return null;
  // `round`/`signed` are the KPI rail's own (`DashboardCards.tsx`), imported rather than
  // repeated: the rule that the sign follows the *printed* figure is one rule, and this
  // file had its own copy of it.
  const value = round(series.value - previous.value, series.fmt);
  // Absent `kind` reads as `share` — the same default the payload's own field documents.
  const share = (series.kind ?? 'share') === 'share';
  const text = share
    ? signed(value, series.fmt)
    : `${value > 0 ? '+' : value < 0 ? '\u2212' : ''}${fmtValue(Math.abs(value), series.fmt, series.unit)}`;
  return {
    value,
    period: previous.period,
    text,
    share,
    direction: value > 0 ? 'up' : value < 0 ? 'down' : '',
  };
}

/** The filter's "View by": the axis a chart's rows are. */
export type BaselineView = 'countries' | 'watersheds';

/** The four shapes a baseline card can be drawn in. */
export type BaselineChartKind = 'bars' | 'grouped' | 'stacked' | 'amount';

/** One series inside a card, with the label the legend prints for it. */
export interface BaselineChartMember {
  series: DashboardBaselineSeries;
  /** What this answer *is* — `Women`, `Rented` — not the whole survey question. */
  label: string;
}

export interface BaselineChartSpec {
  /** `baseline-chart-<id>`: the series id for a lone series, the block id for a group. */
  id: string;
  kind: BaselineChartKind;
  /** The card's title — the series name, or the question the grouped answers share. */
  title: string;
  unit: string;
  fmt: number;
  members: BaselineChartMember[];
}

/**
 * How far a block's answers may miss 100 % and still be read as a composition.
 *
 * A survey question with a "don't know" bucket that was dropped, or four shares rounded to
 * one decimal each, will not total exactly 100. Six points is wide enough for both and far
 * too narrow to catch a block of *independent* practices, which in this survey total
 * anything from 20 % to 300 %.
 */
export const COMPOSITION_TOLERANCE = 6;

/** The least number of answers a question needs before it is drawn as a whole. */
export const COMPOSITION_MIN = 3;

/** The palette a composition's segments are painted from — a small, ordered set (§G8). */
export const COMPOSITION_COLORS = ['#2E7D32', '#2B6CB0', '#C77B21', '#8E3B62', '#5E8C6A', '#8B958A'];

/** The single brand hue the watershed axis is drawn in, and the darker ink for the scope's own. */
export const WATERSHED_COLOR = '#2E7D32';
export const WATERSHED_SELECTED_COLOR = '#1E5B22';

/** Words that end a shared question stem and are not part of it. */
const TRIM = /[\s—–\-:,;(]+$/;

/** Separators a member label may start with once the shared stem is cut off it. */
const LEAD = /^[\s—–\-:,;)(]+/;

/** Lowercase, accent-free — so a French search for `ménages` also finds `menages`. */
export function normalise(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/**
 * The words two or more survey names open with, as the question they are answers to.
 *
 * `["Extension access — women", "Extension access — men"]` is one question asked of two
 * groups, and the card's title is the question. Whole words only: a common *prefix* by
 * character would title a land-tenure card "Land tenure — ownedpledged"-shaped nonsense the
 * moment two answers happened to share a first letter.
 */
export function sharedTitle(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0]!;
  const words = names.map((n) => n.split(' '));
  const first = words[0]!;
  let i = 0;
  while (i < first.length && words.every((w) => w[i] === first[i])) i += 1;
  const stem = first.slice(0, i).join(' ').replace(TRIM, '');
  return stem.length >= 3 ? stem : names[0]!;
}

/** `land_tenure_rented` → `Rented`: the fallback label when a name carries no stem to cut. */
function humanise(token: string): string {
  const text = token.replace(/[_-]+/g, ' ').trim();
  return text ? text[0]!.toUpperCase() + text.slice(1) : '';
}

/** What one member of a group is called once the shared question is off the front of it. */
export function memberLabel(name: string, title: string, sub: string | null | undefined): string {
  if (name !== title && name.startsWith(title)) {
    const rest = name.slice(title.length).replace(LEAD, '').trim();
    if (rest) return rest[0]!.toUpperCase() + rest.slice(1);
  }
  return humanise(sub ?? '') || name;
}

/**
 * A card title with the sheet's own name off the front of it.
 *
 * Under the heading "III.1 Agroforestry", eight cards each titled "Agroforestry — …" spend
 * their first two words repeating the heading, and the words that distinguish them start a
 * third of the way across the card (review, fix round 2). The stem is matched *by word*
 * against the sheet's label, so "Livestock waste — composted" loses "Livestock waste" under
 * the sheet "Livestock waste management" while "Soil practice — crop rotation" under
 * "Agroecological practices" keeps every word it has.
 *
 * The full name never disappears: the card puts it on the heading's own `title`.
 */
export function cardTitle(name: string, sheetLabel: string): string {
  const words = name.split(' ');
  const sheet = sheetLabel.split(' ');
  let i = 0;
  while (i < words.length && i < sheet.length && normalise(words[i]!) === normalise(sheet[i]!)) i += 1;
  if (i === 0 || i === words.length) return name;
  const rest = words.slice(i).join(' ').replace(LEAD, '').trim();
  return rest ? rest[0]!.toUpperCase() + rest.slice(1) : name;
}

/**
 * Whether a block's answers are parts of one whole rather than several practices.
 *
 * The test is the only one that can be made from the payload without a hand-written list of
 * question ids: **they are shares, there are at least three of them, and they add up to
 * about 100 %.** Land tenure (owned + pledged + rented + borrowed) passes; the six
 * agroforestry practices in one sheet, which a household can do all of at once and which
 * total far more than 100, do not — and are drawn as six independent charts, which is what
 * they are. A block with a missing answer fails too: a stack normalised over a hole would
 * silently inflate every segment it does have.
 */
export function isComposition(members: BaselineChartMember[]): boolean {
  if (members.length < COMPOSITION_MIN) return false;
  if (!members.every((m) => (m.series.kind ?? 'share') === 'share')) return false;
  let total = 0;
  for (const m of members) {
    const v = m.series.programme_value ?? m.series.value;
    if (v === null || v === undefined || !Number.isFinite(v)) return false;
    total += v;
  }
  return Math.abs(total - 100) <= COMPOSITION_TOLERANCE;
}

/**
 * Series the survey asks as one question but files as two, keyed by their own ids.
 *
 * The rule below groups by `block`, which is how the workbook says "these are one question's
 * answers" — except that the server numbers blocks *per sheet* and the Extension sheet gives
 * its two series a block each (`1` and `2`) with no `sub` at all, so nothing in the payload
 * says that `mb_ext_female` and `mb_ext_male` are the same question asked of women and of
 * men. They plainly are, and drawing them as two charts side by side asks the reader to do
 * the comparison the chart exists to make.
 *
 * A list rather than a heuristic, because every heuristic available here is worse: the
 * `sub`s are empty, and "a sheet with exactly two share series" would pair any future sheet
 * that happened to have two. Two ids are cheap to read and cheap to correct; if the backend
 * ever gives the pair one block with two `sub`s, the block rule picks it up and this entry
 * becomes dead weight rather than a wrong answer (it is keyed on both ids being present).
 */
export const PAIRS: readonly (readonly [string, string])[] = [['mb_ext_female', 'mb_ext_male']];

/**
 * The cards one sheet's series are drawn as, in the server's own order.
 *
 * The rule, in one sentence: *series sharing a `block` are one card — a pair when the block
 * has two **share** answers, a 100 % stack when it has three or more that add up to a whole,
 * and otherwise nothing at all, each answer going back to being its own chart.* A series with
 * no `block` is always its own card, an `amount` (a yield, a herd, an income) is always a
 * vertical chart, and a `share` is always horizontal bars. Two amounts are never a pair:
 * two yields in one grouped chart would share one axis and the units are not comparable —
 * the docstring's promise that an amount is always vertical is the rule, not a default.
 *
 * `PAIRS` above is the one exception to "grouping comes from `block`", and it is applied
 * first so that a pair the server files in two blocks still reaches the same grouped card.
 */
export function chartPlan(
  series: DashboardBaselineSeries[],
  /**
   * Prefixed onto a grouped card's id.
   *
   * The server numbers blocks *within a sheet* (`"1"`, `"2"`), so two sheets both have a
   * block `"1"` and a card id taken from the block alone would be two elements answering to
   * one `data-testid` on the page. The sheet's own id in front of it makes it unique, and a
   * series' card is already unique because a series id is.
   */
  prefix = '',
): BaselineChartSpec[] {
  const order: string[] = [];
  const blocks = new Map<string, DashboardBaselineSeries[]>();
  /** Series id → the pair it belongs to, for the pairs whose *both* halves are on this sheet. */
  const paired = new Map<string, string>();
  const shareById = new Map(
    series.filter((s) => (s.kind ?? 'share') === 'share').map((s) => [s.id, s]),
  );
  for (const [a, b] of PAIRS) {
    if (shareById.has(a) && shareById.has(b)) {
      paired.set(a, `p:${a}`);
      paired.set(b, `p:${a}`);
    }
  }
  for (const s of series) {
    // A block needs a `sub` to say which answer the series is; without one it is a series
    // that merely mentions a block and cannot be a member of a group.
    const key = paired.get(s.id) ?? (s.block && s.sub ? `b:${s.block}` : `s:${s.id}`);
    if (!blocks.has(key)) {
      blocks.set(key, []);
      order.push(key);
    }
    blocks.get(key)!.push(s);
  }

  const specs: BaselineChartSpec[] = [];
  for (const key of order) {
    const group = blocks.get(key)!;
    if (group.length > 1) {
      const title = sharedTitle(group.map((s) => s.name));
      const members = group.map((s) => ({ series: s, label: memberLabel(s.name, title, s.sub) }));
      // A pair listed in `PAIRS` has no block of its own to be named after — its first
      // series' id is as stable and is already unique on the page.
      const cardId = `${prefix}${key.startsWith('p:') ? group[0]!.id : group[0]!.block!}`;
      const isPair = group.length === 2 && group.every((s) => (s.kind ?? 'share') === 'share');
      if (isPair || isComposition(members)) {
        specs.push({
          id: cardId,
          kind: isPair ? 'grouped' : 'stacked',
          title,
          unit: group[0]!.unit,
          fmt: group[0]!.fmt,
          members,
        });
        continue;
      }
    }
    // One series, or a block whose answers are not a whole: each on its own.
    for (const s of group) {
      specs.push({
        id: s.id,
        kind: (s.kind ?? 'share') === 'amount' ? 'amount' : 'bars',
        title: s.name,
        unit: s.unit,
        fmt: s.fmt,
        members: [{ series: s, label: s.name }],
      });
    }
  }
  return specs;
}

/** One row of a chart's category axis. */
export interface BaselineAxisRow {
  /** ISO3 in the countries view, watershed id in the watersheds view. */
  key: string;
  label: string;
  /** ISO3 either way — a watershed row is coloured and grouped by its country. */
  country: string;
  color: string;
  /** True for the row the page's own scope is: drawn full-strength, the rest at 70 %. */
  selected: boolean;
}

/**
 * Every watershed the section can put on an axis, in the order the payload names them.
 *
 * The roster is normally the payload's own `watersheds`. When it is absent — a `/dashboard`
 * answer cached in the browser from before the filter existed — the series themselves still
 * carry `by_watershed` rows with an id, a name and a country on each, which is the same
 * three fields; this walks the sheets once and takes the first mention of each id. A cached
 * payload therefore loses the *ordering* the server would have given, not the view.
 *
 * (The type's comment promises this fallback; it is implemented here rather than being a
 * promise the reader has to take on trust — review, fix round 2.)
 */
export function watershedRoster(baseline: DashboardBaseline): DashboardBaselineWatershedRef[] {
  if (baseline.watersheds) return baseline.watersheds;
  const seen = new Map<string, DashboardBaselineWatershedRef>();
  for (const sheet of baseline.sheets) {
    for (const series of sheet.series) {
      for (const row of series.by_watershed ?? []) {
        if (!seen.has(row.id)) seen.set(row.id, { id: row.id, name: row.name, country: row.country });
      }
    }
  }
  return [...seen.values()];
}

/** The scope's own country ISO3 and watershed id, read off the payload's rosters. */
export function scopeKeys(baseline: DashboardBaseline): { iso: string | null; watershed: string | null } {
  if (baseline.scope_kind === 'country') {
    const hit = (baseline.countries ?? []).find((c) => c.name === baseline.scope_label);
    return { iso: hit?.iso ?? (COUNTRY_ORDER as string[]).find((i) => i === baseline.scope_label) ?? null, watershed: null };
  }
  if (baseline.scope_kind === 'watershed') {
    const hit = watershedRoster(baseline).find((w) => w.name === baseline.scope_label);
    return { iso: hit?.country ?? null, watershed: hit?.id ?? null };
  }
  return { iso: null, watershed: null };
}

/**
 * The rows every chart in the section shares, for the current view and the page's scope.
 *
 * The filter *composes* with the scope rather than overriding it, which is the whole reason
 * the two controls can sit on one bar:
 *
 * | scope | Countries | Watersheds |
 * |---|---|---|
 * | programme | the four countries | every surveyed watershed |
 * | a country | that country alone, against the programme line | that country's watersheds |
 * | a watershed | the four, its own country full-strength | its country's watersheds, itself darker |
 *
 * The one case with a single row — a country scope in the countries view — is not a chart
 * of one bar for nothing: the dashed programme line beside it is the comparison, which is
 * exactly the question a reader who has just filtered to Mali is asking.
 */
export function baselineAxis(baseline: DashboardBaseline, view: BaselineView): BaselineAxisRow[] {
  const { iso, watershed } = scopeKeys(baseline);
  if (view === 'countries') {
    const roster = baseline.countries ?? COUNTRY_ORDER.map((i) => ({ iso: i, name: i }));
    const ordered = COUNTRY_ORDER.map((code) => roster.find((c) => c.iso === code)).filter(
      (c): c is { iso: string; name: string } => Boolean(c),
    );
    const rows = ordered.map((c) => ({
      key: c.iso,
      label: c.name,
      country: c.iso,
      color: countryColor(c.iso),
      selected: c.iso === iso,
    }));
    // A country scope compares *that* country with the programme, not with its neighbours.
    if (baseline.scope_kind === 'country' && iso) return rows.filter((r) => r.key === iso);
    return rows;
  }

  const all = watershedRoster(baseline);
  const scoped = iso ? all.filter((w) => w.country === iso) : all;
  return scoped.map((w) => ({
    key: w.id,
    label: w.name,
    country: w.country,
    color: w.id === watershed ? WATERSHED_SELECTED_COLOR : WATERSHED_COLOR,
    selected: w.id === watershed,
  }));
}

/** One series' estimate for one axis row — `null` where the survey has none. */
export function axisValue(
  series: DashboardBaselineSeries,
  row: BaselineAxisRow,
  view: BaselineView,
): { value: number | null; n: number | null } {
  if (view === 'countries') {
    const hit = series.by_country.find((c) => c.iso === row.key);
    return { value: hit?.value ?? null, n: hit?.n ?? null };
  }
  const hit = (series.by_watershed ?? []).find((w) => w.id === row.key);
  return { value: hit?.value ?? null, n: hit?.n ?? null };
}

/** A dashed marker across a chart: the programme figure, and the country's at watershed scope. */
export interface BaselineRef {
  kind: 'programme' | 'country';
  value: number;
}

/**
 * The reference lines a card draws — never more than two, and never one that repeats a bar.
 *
 * The programme line is on every chart that has a programme figure: it is what makes a
 * single-country view a comparison instead of a number. The country line is added only in
 * the watersheds view at watershed scope, where the reader is three levels down and the
 * question "and how does that compare with the country" has no bar answering it.
 */
export function baselineRefs(
  series: DashboardBaselineSeries,
  baseline: DashboardBaseline,
  view: BaselineView,
): BaselineRef[] {
  const refs: BaselineRef[] = [];
  const programme = series.programme_value;
  if (programme !== null && programme !== undefined && Number.isFinite(programme)) {
    refs.push({ kind: 'programme', value: programme });
  }
  if (view === 'watersheds') {
    const { iso } = scopeKeys(baseline);
    const hit = iso ? series.by_country.find((c) => c.iso === iso) : undefined;
    if (hit && hit.value !== null && Number.isFinite(hit.value)) {
      refs.push({ kind: 'country', value: hit.value });
    }
  }
  return refs;
}

/** True when not one row of the axis has an estimate — the card prints a sentence instead. */
export function hasEstimate(
  members: BaselineChartMember[],
  rows: BaselineAxisRow[],
  view: BaselineView,
): boolean {
  return members.some((m) => rows.some((r) => axisValue(m.series, r, view).value !== null));
}

/**
 * The sheets the filter leaves, with the series inside them narrowed by the search.
 *
 * A search matches a **block**, not only a series: typing "tenure" must not shatter the
 * land-tenure stack into whichever of its four answers happened to carry the word. Sheets
 * left with nothing are dropped entirely rather than rendered as an empty header.
 */
export function filterSheets(
  sheets: DashboardBaselineSheet[],
  sheetId: string,
  query: string,
): DashboardBaselineSheet[] {
  const picked = sheetId === 'all' ? sheets : sheets.filter((s) => s.group_id === sheetId);
  const q = normalise(query.trim());
  if (!q) return picked;
  return picked
    .map((sheet) => {
      const hitBlocks = new Set(
        sheet.series.filter((s) => normalise(s.name).includes(q)).map((s) => s.block ?? ''),
      );
      const series = sheet.series.filter(
        (s) => normalise(s.name).includes(q) || (s.block ? hitBlocks.has(s.block) : false),
      );
      return { ...sheet, series };
    })
    .filter((sheet) => sheet.series.length > 0);
}

/**
 * Rows in the order a comparison chart prints them: biggest first, missing estimates last.
 *
 * Descending, because the question a country/watershed comparison answers is "who is ahead"
 * and a reader should not have to scan four rows to find the top one. Compositions and
 * amount charts keep the axis' own order instead — see `BaselineCharts.tsx`.
 */
export function sortRows(
  rows: BaselineAxisRow[],
  series: DashboardBaselineSeries,
  view: BaselineView,
): BaselineAxisRow[] {
  return [...rows].sort((a, b) => {
    const va = axisValue(series, a, view).value;
    const vb = axisValue(series, b, view).value;
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    return vb - va;
  });
}
